import React, { useState } from 'react';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Checkbox } from '@vaiu/ui/checkbox';
import { Loader2, Shield } from 'lucide-react';
import { i18n } from '@/utils/i18n';
import { platformApi } from '@/services/platformApi';
import { useToast } from '@/hooks/use-toast';
import { isMmMoneyCurrency } from '@vaiu/utils';

/** Token-like shape for ID token - compatible with parent's Token display */
export interface IDTokenResult {
  key: string;
  type: 'identity';
  amount: number;
  currency: string;
  status: string;
  expiry: string;
  scope?: string[];
  canCredit?: string[];
  expiresAt?: string;
}

interface CreateIDTokenProps {
  onSubmit: (token: IDTokenResult) => void;
  availableCurrencies?: string[];
}

const ID_TOKEN_SCOPES = ['read_profile', 'make_payment'] as const;

/** Profile fields the user can attach to the token. Add new entries here only. */
const ID_TOKEN_INCLUDE_OPTIONS = [
  { key: 'email', labelKey: 'createIDToken.includeEmail' },
  { key: 'phone', labelKey: 'createIDToken.includePhone' },
  { key: 'firstName', labelKey: 'createIDToken.includeFirstName' },
  { key: 'lastName', labelKey: 'createIDToken.includeLastName' },
  { key: 'address', labelKey: 'createIDToken.includeAddress' },
  { key: 'username', labelKey: 'createIDToken.includeUsername' },
] as const;

const CreateIDToken: React.FC<CreateIDTokenProps> = ({
  onSubmit,
  availableCurrencies = ['USD', 'EUR', 'GBP'],
}) => {
  const { toast } = useToast();
  const creditCurrencies = availableCurrencies.filter((c) => !isMmMoneyCurrency(c));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [expiryHours, setExpiryHours] = useState(24);
  const [scope, setScope] = useState<string[]>([]);
  const [canCredit, setCanCredit] = useState<string[]>([]);
  const [include, setInclude] = useState<string[]>([]);

  const scopeLabel = (scopeKey: string): string => {
    const key = `createIDToken.scope.${scopeKey}`;
    const translated = i18n.__(key);
    return translated !== key ? translated : scopeKey.replace(/_/g, ' ');
  };

  const toggleScope = (s: string) => {
    setScope(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    );
  };

  const toggleCanCredit = (c: string) => {
    setCanCredit(prev =>
      prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]
    );
  };

  const toggleInclude = (field: string) => {
    setInclude(prev =>
      prev.includes(field) ? prev.filter(x => x !== field) : [...prev, field]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (scope.length === 0 && canCredit.length === 0 && include.length === 0) {
      toast({
        title: i18n.__('createIDToken.error'),
        description: i18n.__('createIDToken.selectAtLeastOne'),
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        type: 'identity',
        expiryHours,
      };
      if (scope.length > 0) payload.scope = scope;
      if (canCredit.length > 0) {
        payload.canCredit = canCredit.filter((c) => !isMmMoneyCurrency(c));
      }
      if (include.length > 0) payload.include = include;
      if (message?.trim()) payload.message = message.trim();

      const response = await platformApi.tokens.createIdentityToken(payload);
      toast({
        title: i18n.__('createIDToken.success'),
        description: i18n.__('createIDToken.successDesc', { key: response.key }),
      });
      onSubmit({
        key: response.key,
        type: 'identity',
        amount: 0,
        currency: '',
        status: response.status || 'active',
        expiry: response.expiresAt || '',
        scope: response.scope,
        canCredit: response.canCredit,
        expiresAt: response.expiresAt,
      });
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : String(err);
      toast({
        title: i18n.__('createIDToken.error'),
        description: errMsg,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-2 text-muted-foreground mb-4">
        <Shield className="w-4 h-4" />
        <p className="text-sm">{i18n.__('createIDToken.description')}</p>
      </div>

      <div className="space-y-2">
        <Label>{i18n.__('createIDToken.scope')}</Label>
        <p className="text-xs text-muted-foreground mb-2">
          {i18n.__('createIDToken.scopeDescription')}
        </p>
        <div className="flex flex-wrap gap-2">
          {ID_TOKEN_SCOPES.map(s => (
            <label
              key={s}
              className="flex items-center gap-2 text-sm cursor-pointer"
            >
              <Checkbox
                checked={scope.includes(s)}
                onCheckedChange={() => toggleScope(s)}
              />
              <span>{scopeLabel(s)}</span>
            </label>
          ))}
        </div>
        <div className="mt-2 pt-2 border-t border-muted/50">
          <span className="text-sm font-medium">
            {i18n.__('createIDToken.creditCurrencies')}:
          </span>
          <div className="flex flex-wrap gap-2 mt-1">
            {creditCurrencies.map(c => (
              <label
                key={c}
                className="flex items-center gap-2 text-sm cursor-pointer"
              >
                <Checkbox
                  checked={canCredit.includes(c)}
                  onCheckedChange={() => toggleCanCredit(c)}
                />
                <span>{c}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>{i18n.__('createIDToken.expiryHours')}</Label>
        <Input
          type="number"
          min={1}
          max={720}
          value={expiryHours}
          onChange={e => setExpiryHours(parseInt(e.target.value, 10) || 24)}
          onWheel={e => {
            e.currentTarget.blur();
            e.stopPropagation();
          }}
          disabled={isSubmitting}
          className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
      </div>

      <div className="space-y-2">
        <Label>{i18n.__('createIDToken.information')}</Label>
        <p className="text-xs text-muted-foreground mb-2">
          {i18n.__('createIDToken.informationDescription')}
        </p>
        <div className="flex flex-wrap gap-3 text-sm">
          {ID_TOKEN_INCLUDE_OPTIONS.map(({ key, labelKey }) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={include.includes(key)}
                onCheckedChange={() => toggleInclude(key)}
              />
              <span>{i18n.__(labelKey)}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>{i18n.__('common.message')} ({i18n.__('common.optional')})</Label>
        <Input
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder={i18n.__('createToken.messagePlaceholder')}
          disabled={isSubmitting}
        />
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {i18n.__('createToken.creating')}
          </>
        ) : (
          i18n.__('createIDToken.create')
        )}
      </Button>
    </form>
  );
};

export default CreateIDToken;
